import { AlertTriangle, Info } from "lucide-react";
import { emailSyncCounters } from "@/lib/email";
import { formatDuration } from "@/lib/formatters";
import { cn } from "@/lib/utils";

interface Props {
  stats: Record<string, unknown> | null | undefined;
  durationSeconds?: number | null;
  error?: string | null;
  /** One line of counters (the jobs table) instead of a grid (a run's sheet). */
  compact?: boolean;
}

/** What an Email Sync run did: campaigns, contacts, new events, sent, opened,
 *  replies, bounces, errors — then what failed and what the legacy pass did. */
export function EmailSyncRunStats({ stats, durationSeconds, error, compact }: Props) {
  const counters = emailSyncCounters(stats);
  const messages = Array.isArray(stats?.error_messages) ? (stats.error_messages as string[]) : [];
  const legacy = (stats?.legacy ?? null) as { ran?: boolean; reason?: string; error?: string } | null;
  const dryRun = stats?.dry_run === true;

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]" data-testid="email-sync-counters">
        {counters.map((c) => (
          <span key={c.key} className="inline-flex items-baseline gap-1">
            <span
              className={cn(
                "font-semibold tabular-nums",
                c.key === "errors" && c.value > 0 ? "text-rose-600" : "text-slate-800",
              )}
            >
              {c.value}
            </span>
            <span className="text-slate-400">{c.label}</span>
          </span>
        ))}
        {error && (
          <span className="inline-flex min-w-0 items-center gap-1 text-rose-600" title={error}>
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span className="max-w-[420px] truncate">{error}</span>
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl bg-white p-4 shadow-card-rest" data-testid="email-sync-stats">
      {dryRun && (
        <p className="flex items-center gap-1.5 text-xs text-amber-800">
          <Info className="h-3.5 w-3.5" /> Dry run: no Lemlist call was made.
        </p>
      )}
      <div className="grid grid-cols-4 gap-x-4 gap-y-3">
        {counters.map((c) => (
          <div key={c.key} className="space-y-0.5">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{c.label}</div>
            <div
              className={cn(
                "text-[20px] font-semibold tabular-nums",
                c.key === "errors" && c.value > 0 ? "text-rose-600" : "text-slate-900",
              )}
            >
              {c.value}
            </div>
          </div>
        ))}
      </div>
      {durationSeconds != null && (
        <p className="text-xs text-slate-500">Duration {formatDuration(durationSeconds)}</p>
      )}
      {messages.length > 0 && (
        <ul className="space-y-1 rounded-lg bg-rose-50/60 px-3 py-2 text-xs text-rose-700">
          {messages.map((message, index) => (
            <li key={index} className="flex gap-1.5">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{message}</span>
            </li>
          ))}
        </ul>
      )}
      {legacy && (
        <p className="text-xs text-slate-500">
          Legacy enrollments:{" "}
          {legacy.error ? (
            <span className="text-rose-600">{legacy.error}</span>
          ) : legacy.ran ? (
            "synced (company metadata, outside the Email module)"
          ) : (
            legacy.reason ?? "nothing to sync"
          )}
        </p>
      )}
    </div>
  );
}
